import { Add, DeleteForever, Refresh } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Divider,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useErrorDisplay } from '../../components/ErrorDisplay';
import BusySpinner from '../../components/BusySpinner';
import PageTitle from '../../components/PageTitle';
import PanelSegment from '../../components/PanelSegment';
import useViewportMode from '../../hooks/useViewportMode';
import { useAccessInfo } from '../../contexts/AccessTokenProvider';
import { Securables } from '../../Enums';
import ProducerPortalClient from '../../apiClients/ProducerPortalClient';
import {
    ProducerRole,
    ProducerTeamInviteCommand,
    ProducerTeamInvitation,
    ProducerTeamMember,
} from '../../models/producer';

const isActiveState = (state: string): 'success' | 'warning' | 'error' | 'default' => {
    if (state === 'Active')
        return 'success';
    if (state === 'Suspended')
        return 'warning';
    if (state === 'Terminated')
        return 'error';
    return 'default';
};

const ProducerTeamManagementPage = () => {
    const { accessInfo, hasRole } = useAccessInfo();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const { mode, isDesktop } = useViewportMode();
    const canManage = hasRole(Securables.producer_admin, true);
    const [busy, setBusy] = useState(false);
    const [members, setMembers] = useState<ProducerTeamMember[]>([]);
    const [invitations, setInvitations] = useState<ProducerTeamInvitation[]>([]);
    const [invite, setInvite] = useState<{ email: string; role: ProducerRole }>({ email: '', role: 'Producer' });
    const [notice, setNotice] = useState('');

    const load = async () => {
        if (!accessInfo.selectedProducerCompanyId)
            return;

        try {
            setBusy(true);
            const [teamMembers, teamInvites] = await Promise.all([
                new ProducerPortalClient().GetTeamMembers(accessInfo.selectedProducerCompanyId),
                new ProducerPortalClient().GetTeamInvitations(accessInfo.selectedProducerCompanyId),
            ]);
            setMembers(teamMembers);
            setInvitations(teamInvites);
        } catch (error) {
            setError(error, 'Failed to load team members and invitations');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessInfo.selectedProducerCompanyId]);

    const inviteTeamMember = async () => {
        if (!canManage)
            return;
        if (!accessInfo.selectedProducerCompanyId || !invite.email)
            return;

        try {
            setBusy(true);
            const payload: ProducerTeamInviteCommand = {
                EmailAddress: invite.email,
                Role: invite.role,
            };
            await new ProducerPortalClient().InviteTeamMember(accessInfo.selectedProducerCompanyId, payload);
            setNotice('Invitation sent. Keep this secure invite visible until the recipient accepts it from their inbox.');
            setInvite({ email: '', role: 'Producer' });
            await load();
        } catch (error) {
            setError(error, 'We could not send this team invitation.');
        } finally {
            setBusy(false);
        }
    };

    const resend = async (invitationId: string) => {
        try {
            setBusy(true);
            await new ProducerPortalClient().ResendTeamInvitation(invitationId);
            await load();
        } catch (error) {
            setError(error, 'Could not resend this team invitation.');
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (invitationId: string) => {
        try {
            setBusy(true);
            await new ProducerPortalClient().RevokeTeamInvitation(invitationId);
            await load();
        } catch (error) {
            setError(error, 'Could not revoke this team invitation.');
        } finally {
            setBusy(false);
        }
    };

    return <Box data-viewport-mode={mode}>
        <ErrorDisplay />
        <BusySpinner busy={busy}>
            <PanelSegment titleContent={<PageTitle title="Producer team" />} toolbar={
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button component={Link} to="/ProducerPortal" size="small" variant="outlined" sx={{ minHeight: isDesktop ? undefined : 44 }}>
                        Dashboard
                    </Button>
                    <Button component={Link} to="/ProducerPortal/ClientCompanies" size="small" variant="outlined" sx={{ minHeight: isDesktop ? undefined : 44 }}>
                        Clients
                    </Button>
                    <Button startIcon={<Refresh />} size="small" variant="text" onClick={() => void load()} sx={{ minHeight: isDesktop ? undefined : 44 }}>
                        Refresh
                    </Button>
                </Stack>
            }>
                <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Manage teammate access for your assigned client assignments. Membership state controls payroll, assessment access, and customer operations permissions.
                    </Typography>

                    {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Invite a team member</Typography>
                            <Grid container spacing={1.25}>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Email address" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} size="small" fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField select label="Producer role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value as ProducerRole })} size="small" fullWidth>
                                        <MenuItem value="Producer">Producer</MenuItem>
                                        <MenuItem value="ProducerAdmin">Producer Admin</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={2} sx={{ display: 'flex' }}>
                                    <Button disabled={!canManage || !invite.email} onClick={inviteTeamMember} variant="contained" startIcon={<Add />} sx={{ minHeight: 40, alignSelf: 'center' }} fullWidth>
                                        Send
                                    </Button>
                                </Grid>
                            </Grid>
                            {!canManage && <Typography variant="caption" color="text.secondary">Only Producer Admins can send new team invitations.</Typography>}
                        </CardContent>
                    </Card>

                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Active team members</Typography>
                    <Stack spacing={1}>
                        {members.map(member => <Card key={member.Id} variant="outlined">
                            <CardContent>
                                <Stack direction={isDesktop ? 'row' : 'column'} spacing={1} alignItems={isDesktop ? 'center' : 'flex-start'} justifyContent="space-between">
                                    <Box>
                                        <Typography fontWeight={700}>{member.FullName}</Typography>
                                        <Typography variant="caption" color="text.secondary">{member.EmailAddress}</Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip size="small" color={isActiveState(member.State)} label={member.State} />
                                        <Chip size="small" label={member.Role} />
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>)}
                        {members.length === 0 && <Typography color="text.secondary">No active team members were found for this producer workspace.</Typography>}
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" sx={{ mb: 1 }}>Team invitations</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Expired, revoked, and duplicate invites are shown here so you can resend or revoke safely without touching private client identities.
                    </Typography>

                    <Stack spacing={1}>
                        {invitations.map(invitation => <Card key={invitation.Id} variant="outlined">
                            <CardContent>
                                <Stack direction={isDesktop ? 'row' : 'column'} spacing={1} alignItems={isDesktop ? 'center' : 'flex-start'} justifyContent="space-between">
                                    <Box>
                                        <Typography fontWeight={700}>{invitation.EmailAddress}</Typography>
                                        <Typography variant="caption" color="text.secondary">{invitation.Role}</Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip size="small" color={isActiveState(invitation.State)} label={invitation.State} />
                                        <CardActions>
                                            <Button size="small" onClick={() => void resend(invitation.Id)} disabled={!canManage}>Resend</Button>
                                            <Button size="small" color="error" startIcon={<DeleteForever />} onClick={() => void revoke(invitation.Id)} disabled={!canManage}>Revoke</Button>
                                        </CardActions>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>)}
                        {invitations.length === 0 && <Typography color="text.secondary">No active team invites.</Typography>}
                    </Stack>
                </Box>
            </PanelSegment>
        </BusySpinner>
    </Box>;
};

export default ProducerTeamManagementPage;
